import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Bell, Check, CheckCheck, X, Target, ExternalLink, MessageSquare } from "lucide-react";

import { KoraLoader } from "../KoraLoader";
import { API_BASE as API } from "../../utils/api";

export default function NotificationBell() {
  const { language } = useLanguage();
  const isEs = language === "es";
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const lastFetchRef = useRef(0);

  const fetchUnreadCount = useCallback(async () => {
    // Throttle: don't fetch if last fetch was less than 10 seconds ago
    const now = Date.now();
    if (now - lastFetchRef.current < 10000) return;
    lastFetchRef.current = now;
    
    try {
      const response = await axios.get(`${API}/api/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      // Silently fail - user might not be logged in
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    
    // Generate follow-up reminders on initial load (once per session)
    const generateFollowUpReminders = async () => {
      try {
        await axios.post(`${API}/api/notifications/generate-follow-up-reminders`, {}, { withCredentials: true });
        // Refresh count after generating
        setTimeout(fetchUnreadCount, 1000);
      } catch (err) {
        // Silently fail
      }
    };
    generateFollowUpReminders();
    
    // Poll for new notifications every 60 seconds (reduced from 30)
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/notifications?limit=20`, { withCredentials: true });
      setNotifications(response.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/api/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/api/notifications/mark-all-read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    setIsOpen(false);
    
    // Use the smart /open endpoint that handles cross-workspace switching safely.
    // Falls back to legacy behavior (read + navigate) on error.
    try {
      const res = await axios.post(
        `${API}/api/notifications/${notification.notification_id}/open`,
        {},
        { withCredentials: true }
      );
      const data = res.data || {};
      
      // Update UI state locally
      setNotifications(prev =>
        prev.map(n => n.notification_id === notification.notification_id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - (notification.read ? 0 : 1)));
      
      // If workspace was switched, show a heads-up and force a full reload so
      // the whole app re-scopes to the new company_id.
      if (data.switched) {
        const targetName = data.target_company_name || "otra empresa";
        alert(isEs
          ? `Cambiando a la empresa "${targetName}". La app se recargará.`
          : `Switching to company "${targetName}". App will reload.`);
        // Force reload with the target link so React state resets
        window.location.href = data.link || "/";
        return;
      }
      
      // No switch needed. If backend said "no_membership", warn the user.
      if (data.success === false && data.message === "no_membership") {
        alert(isEs
          ? `Esta notificación es de "${data.target_company_name || 'otra empresa'}" y no tienes acceso a ese workspace.`
          : `This notification is from "${data.target_company_name || 'another company'}" but you don't have access to that workspace.`);
        return;
      }
      
      // Normal (same-company) navigation
      const link = data.link || notification.link;
      if (link && typeof link === "string") {
        setTimeout(() => {
          if (link.startsWith("/")) navigate(link);
          else if (link.startsWith("http")) window.open(link, "_blank");
        }, 150);
      }
    } catch (e) {
      // Fallback: just do the old behavior
      console.error("open notification failed, fallback:", e);
      if (!notification.read) markAsRead(notification.notification_id);
      const link = notification.link;
      if (link && typeof link === "string") {
        setTimeout(() => {
          if (link.startsWith("/")) navigate(link);
          else if (link.startsWith("http")) window.open(link, "_blank");
        }, 150);
      }
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return isEs ? "Ahora" : "Just now";
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "campaign_access_granted":
        return <Target className="w-4 h-4 text-purple-500" />;
      case "follow_up_reminder":
        return <Bell className="w-4 h-4 text-amber-600" />;
      case "mention":
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button 
        variant="ghost" 
        size="icon" 
        className="relative"
        onClick={handleOpen}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute left-0 bottom-full mb-2 w-80 bg-background border rounded-lg shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-muted/50">
            <h3 className="font-semibold text-sm text-blue-900">
              {isEs ? "Notificaciones" : "Notifications"}
            </h3>
            {unreadCount > 0 && (
              <Button 
                size="sm" 
                className="text-xs h-7 bg-[#AED768] hover:bg-[#9BC75A] text-blue-900 font-medium"
                onClick={markAllAsRead}
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                {isEs ? "Marcar todas" : "Mark all read"}
              </Button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <KoraLoader size={20} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                {isEs ? "No hay notificaciones" : "No notifications"}
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  className={`p-3 border-b hover:bg-muted/50 cursor-pointer transition-colors ${
                    !notification.read ? "bg-blue-50 dark:bg-blue-900/10" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm text-gray-900 dark:text-gray-100 ${!notification.read ? "font-semibold" : ""}`}>
                          {notification.title?.replace(/^[⚠️📅🔔]\s*/, '')}
                        </p>
                        {notification.priority === "high" && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded">
                            {isEs ? "Urgente" : "Urgent"}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      {notification.company_name && (
                        <p className="text-[10px] text-violet-700 mt-1 truncate">
                          🏢 {notification.company_name}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          {formatTime(notification.created_at)}
                        </span>
                        {notification.link && (
                          <ExternalLink className="w-3 h-3 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    {!notification.read && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t bg-muted/50">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => setIsOpen(false)}
              >
                {isEs ? "Cerrar" : "Close"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
